<template>
  <div class="container cart-page">
    <h1>{{ $t('cart.title') }}</h1>
    
    <div v-if="cart.length === 0" class="empty-cart glass">
      <p>{{ $t('cart.empty_message') }}</p>
      <NuxtLink :to="localePath('/products')" class="btn btn-primary">{{ $t('cart.start_browsing') }}</NuxtLink>
    </div>

    <div v-else class="cart-layout">
      <div class="cart-items glass">
        <div v-for="item in cart" :key="item._id" class="cart-item">
          <img :src="proxiedUrl(item.imageUrl)" :alt="item.title" class="item-thumb" />
          <div class="item-details">
            <h3>{{ item.title }}</h3>
            <p class="category">{{ item.category }}</p>
          </div>
          <div class="item-price">${{ item.price }}</div>
          <button @click="removeFromCart(item._id)" class="remove-btn">×</button>
        </div>
      </div>

      <div class="cart-summary glass">
        <h2>{{ $t('cart.summary') }}</h2>
        <div class="summary-row">
          <span>{{ $t('cart.subtotal') }}</span>
          <span>${{ total }}</span>
        </div>
        <div class="summary-row">
          <span>{{ $t('cart.tax') }}</span>
          <span>$0.00</span>
        </div>
        <div class="total-row">
          <span>{{ $t('cart.total') }}</span>
          <span>${{ total }}</span>
        </div>
        <button @click="checkout" class="btn btn-primary w-full checkout-btn" :disabled="checkingOut">
          {{ checkingOut ? $t('cart.processing') : $t('cart.checkout') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
const proxiedUrl = (url) => {
    if (!url || typeof url !== 'string') return ''
    if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
    return url
}

const { cart, removeFromCart, total, clearCart } = useCart()
const { user } = useAuth()
const router = useRouter()
const { t } = useI18n()
const checkingOut = ref(false)
const localePath = useLocalePath()

const checkout = async () => {
  if (!user.value) {
    const { $toast } = useNuxtApp()
    $toast.info(t('cart.login_required'))
    router.push(localePath('/auth') + '?redirect=/cart')
    return
  }

  checkingOut.value = true
  try {
    const { success } = await $fetch('/api/orders', {
      method: 'POST',
      body: {
        items: cart.value.map(item => ({
          product: item._id,
          title: item.title,
          price: item.price
        }))
      }
    })

    if (success) {
      clearCart()
      // Same dead URL as the landing nav's Dashboard button, on a worse
      // page: this fires the moment a buyer has paid. Their order lives
      // on the storefront, and a cross-subdomain hop needs a real
      // navigation — router.push cannot leave this route tree.
      window.location.href = getSubdomainUrl('shop', '/orders')
      const { $toast } = useNuxtApp()
      $toast.success(t('cart.order_success'))
    }
  } catch (error) {
    const { $toast } = useNuxtApp()
    $toast.error(t('cart.checkout_failed') + (error.data?.message || error.message))
  }
  checkingOut.value = false
}


// Tenant-aware page title — pre-2026-05 this was hardcoded as
// "SoundForge" (an old brand name) on every tenant. Pull the active
// tenant's appName from the global state populated by the subdomain
// middleware; fall back to "Diztree" when state isn't ready (SSR-only
// edge cases).
// No type argument here: this block is a plain `<script setup>`, not
// `lang="ts"`. A generic survives compilation and is then evaluated as a
// comparison at runtime, so `any` is looked up as a variable and the page
// dies with "any is not defined" — server-rendered, i.e. a 500 rather than
// a console warning. Type-checking never catches it either.
const tenantState = useState('tenant')
useHead({
  title: () => `Shopping Cart - ${tenantState.value?.theme?.appName || 'Diztree'}`
})
</script>

<style scoped>
.cart-page {
  padding: var(--space-8) var(--space-4);
  max-width: 1000px;
}

h1 {
  font-family: var(--font-heading);
  margin-bottom: var(--space-6);
}

.empty-cart {
  padding: var(--space-12);
  text-align: center;
  border-radius: var(--radius-lg);
}

.empty-cart p {
  margin-bottom: var(--space-6);
  color: var(--text-muted);
}

.cart-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-8);
}

@media (max-width: 768px) {
  .cart-layout {
    grid-template-columns: 1fr;
  }
}

.cart-items {
  padding: var(--space-6);
  border-radius: var(--radius-lg);
}

.cart-item {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding-bottom: var(--space-4);
  margin-bottom: var(--space-4);
  border-bottom: 1px solid var(--border);
}

.cart-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.item-thumb {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-sm);
  object-fit: cover;
}

.item-details {
  flex: 1;
}

.item-details h3 {
  font-size: 1rem;
  margin-bottom: 2px;
}

.item-price {
  font-weight: bold;
  font-size: 1.1rem;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0 var(--space-2);
}

.remove-btn:hover {
  color: var(--accent);
}

.cart-summary {
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  height: fit-content;
}

.cart-summary h2 {
  font-size: 1.25rem;
  margin-bottom: var(--space-4);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-2);
  color: var(--text-muted);
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border);
  font-weight: bold;
  font-size: 1.25rem;
  margin-bottom: var(--space-6);
}

.w-full {
  width: 100%;
}

.checkout-btn {
  font-size: 1rem;
  padding: var(--space-3);
}
</style>
